import { useState, useRef, useEffect } from "react";
import Header from "@/components/Header";
import ChatMessage from "@/components/ChatMessage";
import SupportAttachment from "@/components/SupportAttachment";
import { useSupportChat } from "@/hooks/useSupportChat";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Trash2, Bot, Loader2, Headphones, X, LogIn } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

const AIChat = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const { 
    messages, isLoading, error, sendMessage, clearChat,
    isSupportMode, supportRequest, requestSupport, cancelSupport, isGuest
  } = useSupportChat();
  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState<{ url: string; type: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() || attachment) {
      sendMessage(input, attachment?.url, attachment?.type);
      setInput("");
      setAttachment(null);
    }
  };

  const handleAttachmentUpload = (url: string, type: string) => {
    if (url) setAttachment({ url, type }); else setAttachment(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-[600px] h-[600px] bg-crypto-purple/5 rounded-full blur-3xl" />
      </div>

      <main className="container mx-auto px-4 pt-24 pb-6 relative z-10 flex-1 flex flex-col max-w-4xl">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${isSupportMode ? "bg-primary/20" : "bg-crypto-gold/20"}`}>
                {isSupportMode ? <Headphones className="w-5 h-5 sm:w-6 sm:h-6 text-primary" /> : <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-crypto-gold" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg sm:text-2xl font-bold text-foreground">
                    {isSupportMode ? t('chat.liveSupport') : t('chat.title')}
                  </h1>
                  {isSupportMode && (
                    <Badge variant="default" className="bg-primary text-xs">
                      {supportRequest?.status === "pending" ? t('chat.waiting') : t('chat.connected')}
                    </Badge>
                  )}
                  {isGuest && <Badge variant="secondary" className="text-xs">{t('chat.guest')}</Badge>}
                </div>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {isSupportMode ? t('chat.supportChat') : t('chat.askAboutCrypto')}
                </p>
              </div>
            </div>
            {isGuest && (
              <Link to="/auth">
                <Button variant="outline" size="sm" className="gap-2">
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('auth.signIn')}</span>
                </Button>
              </Link>
            )}
          </div>
          
          <div className="overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            <div className="flex items-center gap-2 w-max">
              {isSupportMode ? (
                <Button variant="outline" size="sm" onClick={cancelSupport} className="text-destructive border-destructive/50 hover:bg-destructive/10 text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
                  <X className="w-4 h-4 ml-1 sm:ml-2" />{t('chat.cancelSupport')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={requestSupport} className="text-primary border-primary/50 hover:bg-primary/10 text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
                  <Headphones className="w-4 h-4 ml-1 sm:ml-2" />{t('chat.requestSupport')}
                </Button>
              )}
              {messages.length > 0 && (
                <Button variant="outline" size="sm" onClick={clearChat} className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
                  <Trash2 className="w-4 h-4 ml-1 sm:ml-2" />{t('chat.clearChat')}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 glass rounded-xl overflow-hidden flex flex-col">
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Bot className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{t('chat.welcomeTitle')}</h3>
                <p className="text-muted-foreground text-sm max-w-md">{t('chat.welcomeDesc')}</p>
                <div className="flex flex-wrap gap-2 mt-6 justify-center">
                  {[t('chat.suggestion1'), t('chat.suggestion2'), t('chat.suggestion3')].map((suggestion) => (
                    <Button key={suggestion} variant="secondary" size="sm" onClick={() => sendMessage(suggestion)} className="text-xs">{suggestion}</Button>
                  ))}
                </div>
                <div className="mt-8 p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-sm text-muted-foreground">
                    {t('chat.needHuman')}{" "}
                    <button onClick={requestSupport} className="text-primary hover:underline font-medium">{t('chat.requestSupport')}</button>
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <ChatMessage key={i} role={msg.role} content={msg.content} isSupport={msg.role === "support"} adminName={msg.adminName} adminEmail={msg.adminEmail} attachmentUrl={msg.attachmentUrl} attachmentType={msg.attachmentType} />
                ))}
                {isLoading && messages[messages.length - 1]?.role === "user" && !isSupportMode && (
                  <div className="flex items-center gap-2 text-muted-foreground p-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{t('chat.typing')}</span>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {error && <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm text-center">{error}</div>}

          <form onSubmit={handleSubmit} className="p-4 border-t border-border flex gap-2 items-center">
            {isSupportMode && <SupportAttachment onUpload={handleAttachmentUpload} disabled={isLoading && !isSupportMode} />}
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={isSupportMode ? t('chat.supportPlaceholder') : t('chat.inputPlaceholder')} disabled={isLoading && !isSupportMode} className="flex-1" />
            <Button type="submit" disabled={(isLoading && !isSupportMode) || (!input.trim() && !attachment)}>
              {isLoading && !isSupportMode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AIChat;
